import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../context/LanguageContext';
import { ROUTES } from '../../../../routes/Paths';
import { initializeCatalogSection, initializeCatalogVideoPopup, CatalogImage } from '../script/catalog-section';
import { initializeTitleAnimation } from '../script/title-animation';
import { initializeCatalogMobileSlider } from '../script/catalog-mobile-slider';
import CatalogPreviewModal from './CatalogPreviewModal';
import '../style/catalog-section.css';
import { trackPhoneClick } from '../../../../../presentation/components/email/utils/trackingUtils';

// Get the display text for the current language from a CatalogImage
const getCatalogText = (image: CatalogImage, lang: string): string => {
    const t = image.catalogImageText;
    if (!t) return '';
    if (typeof t === 'string') return t;
    return (lang === 'en' ? t.en : t.pt) || t.pt || '';
};

// Render text with <r>...</r> tags as red spans
const renderCatalogText = (markedText: string): React.ReactNode => {
    if (!markedText) return null;
    const parts = markedText.split(/(<r>.*?<\/r>)/g);
    return parts.map((part, i) => {
        const match = part.match(/^<r>(.*)<\/r>$/);
        if (match) {
            return <span key={i} style={{ color: '#ea2127' }}>{match[1]}</span>;
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });
};

const CatalogSection: React.FC = () => {
    const { t, language } = useLanguage();
    const containerRef = useRef<HTMLElement>(null);
    const slidingTextRef = useRef<HTMLUListElement>(null);
    const videoLinkRef = useRef<HTMLAnchorElement>(null);
    const [catalogImages, setCatalogImages] = useState<CatalogImage[]>([]);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalCurrentPage, setModalCurrentPage] = useState(0);
    const [isDesktop, setIsDesktop] = useState(false);

    // Get translations - the proxy automatically resolves the current language
    // Use useMemo to ensure translations update when language changes
    const catalogTranslationsObj = useMemo(() => {
        const catalogTranslations = t.CATALOG;
        const servicesTranslations = t.SERVICES;
        const navigationTranslations = t.NAVIGATION;
        const contactTranslations = t.CONTACT;

        return {
            slidingText: catalogTranslations.slidingText,
            tagline: catalogTranslations.tagline,
            title: catalogTranslations.title,
            text: catalogTranslations.text,
            premiumQualityProducts: servicesTranslations.premiumQualityProducts,
            expertRecommendations: servicesTranslations.expertRecommendations,
            bestPricesGuaranteed: servicesTranslations.bestPricesGuaranteed,
            specialPromotions: servicesTranslations.specialPromotions,
            needHelpChoosing: servicesTranslations.needHelpChoosing,
            contactUsBtn: navigationTranslations.contact,
            clickToViewFullCatalog: catalogTranslations.clickToViewFull,
            roadyMontijoCatalog: catalogTranslations.previewTitle,
            october2025Edition: catalogTranslations.previewEdition,
            downloadFullPDF: catalogTranslations.downloadFullPDF,
            phoneCallInfo: contactTranslations.phoneCallInfo
        };
    }, [t]);

    // Preload first image when catalog images are loaded
    useEffect(() => {
        if (catalogImages.length > 0 && catalogImages[0]?.catalogImageUrl) {
            const img = new Image();
            img.src = catalogImages[0].catalogImageUrl;
        }
    }, [catalogImages]);

    useEffect(() => {
        setIsDesktop(window.innerWidth > 991);
        
        const handleResize = () => {
            setIsDesktop(window.innerWidth > 991);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;

        initializeCatalogSection(
            containerRef.current,
            slidingTextRef.current,
            setCatalogImages,
            setVideoUrl,
            setCurrentPage,
            setIsModalOpen,
            setModalCurrentPage
        );
    }, []);

    // Initialize title animation only once on first page load
    useEffect(() => {
        // Check if this is the first time initializing (use a flag in sessionStorage)
        const hasInitialized = sessionStorage.getItem('catalog-titles-initialized');
        
            const titleTimer = setTimeout(() => {
            if (!hasInitialized) {
            initializeTitleAnimation();
                sessionStorage.setItem('catalog-titles-initialized', 'true');
            } else {
                // On language change, just make titles visible without animation
                const titles = document.querySelectorAll('.catalog-section .title-animation');
                titles.forEach((title: Element) => {
                    const el = title as HTMLElement;
                    el.style.opacity = '1';
                });
            }
            
            // Refresh ScrollTrigger to recalculate positions
            if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
                (window as any).ScrollTrigger.refresh();
            }
        }, 200);

            return () => {
                clearTimeout(titleTimer);
            };
    }, [language]); // Re-run when language changes

    // Expose catalog images to window for mobile slider and initialize immediately on mobile
    useEffect(() => {
        if (catalogImages.length > 0) {
            (window as any).catalogImages = catalogImages;
            
            // Initialize mobile slider immediately after catalog images are available (on mobile)
            if (window.innerWidth <= 991 && containerRef.current) {
                // Small delay to ensure DOM is ready, but much faster than before
                setTimeout(() => {
                    initializeCatalogMobileSlider(containerRef.current);
                    // Event will be dispatched by the mobile slider itself after initialization
                }, 100);
            }
        }
    }, [catalogImages]);

    // Initialize mobile slider on window resize (only if crossing mobile threshold)
    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        let wasMobile = window.innerWidth <= 991;
        const resizeHandler = () => {
            const isMobile = window.innerWidth <= 991;
            if (isMobile !== wasMobile) {
                wasMobile = isMobile;
                if (isMobile && catalogImages.length > 0) {
                    setTimeout(() => {
                        initializeCatalogMobileSlider(containerRef.current);
                    }, 100);
                }
            }
        };

        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, [catalogImages.length]);

    // Sync currentPage with desktop swiper slide changes
    useEffect(() => {
        const handleSlideChange = (e: Event) => {
            const index = (e as CustomEvent).detail;
            setCurrentPage(index);
        };
        document.addEventListener('catalogDesktopSlideChange', handleSlideChange);
        return () => document.removeEventListener('catalogDesktopSlideChange', handleSlideChange);
    }, []);

    // Expose functions to window for catalog navigation
    useEffect(() => {
        const changeCatalogPage = (direction: number) => {
            if (catalogImages.length === 0) return;
            setCurrentPage((prev) => {
                const newPage = prev + direction;
                if (newPage < 0) return catalogImages.length - 1;
                if (newPage >= catalogImages.length) return 0;
                return newPage;
            });
        };

        const openCatalogModal = () => {
            // Products mode shows generic product images — the catalog PDF
            // preview modal makes no sense there, so clicks do nothing.
            if ((window as any).catalogProductsMode) return;
            setModalCurrentPage(currentPage);
            setIsModalOpen(true);
        };

        const closeCatalogModal = () => {
            setIsModalOpen(false);
        };

        const changeCatalogPreviewPage = (direction: number) => {
            if (catalogImages.length === 0) return;
            setModalCurrentPage((prev) => {
                const newPage = prev + direction;
                if (newPage < 0) return catalogImages.length - 1;
                if (newPage >= catalogImages.length) return 0;
                return newPage;
            });
        };

        (window as any).changeCatalogPage = changeCatalogPage;
        (window as any).openCatalogModal = openCatalogModal;
        (window as any).closeCatalogModal = closeCatalogModal;
        (window as any).changeCatalogPreviewPage = changeCatalogPreviewPage;

        return () => {
            delete (window as any).changeCatalogPage;
            delete (window as any).openCatalogModal;
            delete (window as any).closeCatalogModal;
            delete (window as any).changeCatalogPreviewPage;
        };
    }, [catalogImages, currentPage]);

    // Set video URL and data attributes when video is loaded, then reinitialize popup
    useEffect(() => {
        if (videoUrl && videoLinkRef.current && containerRef.current) {
            videoLinkRef.current.setAttribute('href', 'javascript:void(0)');
            videoLinkRef.current.setAttribute('data-video-url', videoUrl);
            videoLinkRef.current.setAttribute('data-video-source', 'firebase');
            // Check if it's a Facebook video
            const isFacebookVideo = videoUrl.includes('facebook.com') || videoUrl.includes('fb.com');
            videoLinkRef.current.setAttribute('data-video-type', isFacebookVideo ? 'facebook' : 'other');
            
            // Reinitialize video popup after setting the URL
            // This ensures the popup uses the correct video URL with autoplay
            setTimeout(() => {
                if (containerRef.current) {
                    initializeCatalogVideoPopup(containerRef.current);
                }
            }, 300);
        }
    }, [videoUrl]);

    const currentImage = catalogImages[currentPage] || null;

    const handleModalPageChange = (direction: number) => {
        if (catalogImages.length === 0) return;
        setModalCurrentPage((prev) => {
            const newPage = prev + direction;
            if (newPage < 0) return catalogImages.length - 1;
            if (newPage >= catalogImages.length) return 0;
            return newPage;
        });
    };

    return (
        <>
            <section className="catalog-section" ref={containerRef}>
                <ul className="catalog-section__sliding-text-list list-unstyled marquee_mode-1" ref={slidingTextRef}>
                    {/* Multiple instances for seamless infinite loop */}
                    {[...Array(10)].map((_, index) => (
                        <li key={index}>
                            <span data-hover="Catalog" className="catalog-section__sliding-text-title">
                                {catalogTranslationsObj.slidingText}
                            </span>
                        </li>
                    ))}
                </ul>
                <div className="catalog-section__right-bg"></div>
                <div className="container">
                    <div className="row">
                        <div className="col-xl-6">
                            <div className="catalog-section__left">
                                <div className="section-title text-left sec-title-animation animation-style2">
                                    <div className="section-title__tagline-box">
                                        <div className="section-title__tagline-border">
                                            <div className="section-title__shape-1">
                                                <i className="section-title__circle"></i>
                                            </div>
                                        </div>
                                        <h6 className="section-title__tagline">{catalogTranslationsObj.tagline}</h6>
                                        <div className="section-title__tagline-border">
                                            <div className="section-title__shape-2">
                                                <i className="section-title__circle"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <h3 
                                        className="section-title__title title-animation" 
                                        key={`catalog-title-${language}`}
                                        dangerouslySetInnerHTML={{ __html: catalogTranslationsObj.title }}
                                    />
                                </div>
                                <p 
                                    className="catalog-section__text" 
                                    dangerouslySetInnerHTML={{ __html: catalogTranslationsObj.text }}
                                />

                                <div className="catalog-section__points-box">
                                    <div className="catalog-section__points-list-shape-1"></div>
                                    <ul className="catalog-section__points-list list-unstyled">
                                        <li>
                                            <div className="icon">
                                                <span className="icon-tools"></span>
                                            </div>
                                            <p dangerouslySetInnerHTML={{ __html: catalogTranslationsObj.premiumQualityProducts }} />
                                        </li>
                                        <li>
                                            <div className="icon">
                                                <span className="icon-technician"></span>
                                            </div>
                                            <p dangerouslySetInnerHTML={{ __html: catalogTranslationsObj.expertRecommendations }} />
                                        </li>
                                    </ul>
                                    <ul className="catalog-section__points-list list-unstyled">
                                        <li>
                                            <div className="icon">
                                                <span className="icon-satisfaction"></span>
                                            </div>
                                            <p dangerouslySetInnerHTML={{ __html: catalogTranslationsObj.bestPricesGuaranteed }} />
                                        </li>
                                        <li>
                                            <div className="icon">
                                                <span className="icon-affordable"></span>
                                            </div>
                                            <p dangerouslySetInnerHTML={{ __html: catalogTranslationsObj.specialPromotions }} />
                                        </li>
                                    </ul>
                                </div>
                                <div className="catalog-section__author-box">
                                    <div className="catalog-section__author-content">
                                        <p>{catalogTranslationsObj.needHelpChoosing}</p>
                                        <p>
                                            <a href="tel:+351276 249290" style={{ color: '#ea2127', textDecoration: 'none' }} onClick={() => trackPhoneClick('hero_homepage')}>(+351) 276 249 290</a>
                                            <span 
                                                className="info-icon phone-info-icon" 
                                                data-tooltip="phoneCallInfo"
                                                data-tooltip-text={catalogTranslationsObj.phoneCallInfo}
                                            >
                                                <i className="fas fa-info-circle"></i>
                                            </span>
                                        </p>
                                        <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', position: 'relative' }}>
                                            <Link to={ROUTES.CONTACT} className="thm-btn" style={{ textDecoration: 'none' }}>
                                                {catalogTranslationsObj.contactUsBtn}
                                                <span><i className="icon-next"></i></span>
                                            </Link>
                                            <div className="catalog-video-link" style={{ marginLeft: '150px' }}>
                                                <a 
                                                    href="javascript:void(0)" 
                                                    className="catalog-video-popup video-popup"
                                                    ref={videoLinkRef}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        return false;
                                                    }}
                                                    data-video-url={videoUrl || ""}
                                                >
                                                    <div className="catalog-video-icon">
                                                        <span className="fa fa-play"></span>
                                                        <i className="ripple"></i>
                                                    </div>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-6">
                            <div className="catalog-section__right">
                                <div className="catalog-carousel">
                                    <div className="catalog-navigation">
                                        <button 
                                            className="catalog-nav-btn catalog-nav-prev" 
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                        <button 
                                            className="catalog-nav-btn catalog-nav-next" 
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </div>
                                    {/* Desktop Swiper Container */}
                                    {isDesktop && (
                                        <div
                                            className="catalog-desktop-swiper-container"
                                            style={{
                                                width: '100%',
                                                marginTop: '70px',
                                                overflow: 'hidden',
                                                position: 'relative'
                                            }}
                                        >
                                            <div
                                                className="swiper catalog-desktop-swiper"
                                                style={{
                                                    width: '100%',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <div
                                                    className="swiper-wrapper"
                                                    style={{
                                                        display: 'flex',
                                                        width: '100%'
                                                    }}
                                                >
                                                    {catalogImages.length > 0 ? (
                                                        catalogImages.map((image, index) => (
                                                            <div
                                                                key={image.id || index}
                                                                className="swiper-slide"
                                                                style={{
                                                                    width: '100%',
                                                                    flexShrink: 0,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                {getCatalogText(image, language) && (
                                                                    <h3 style={{
                                                                        margin: '0 0 30px 0',
                                                                        color: '#fff',
                                                                        fontSize: '36px',
                                                                        fontWeight: 700,
                                                                        textAlign: 'center',
                                                                        lineHeight: '1.2em',
                                                                        textTransform: 'none',
                                                                        width: '100%'
                                                                    }}>
                                                                        {renderCatalogText(getCatalogText(image, language))}
                                                                    </h3>
                                                                )}
                                    <div
                                        className="catalog-item"
                                                                    onClick={() => (window as any).openCatalogModal?.()}
                                                                    style={{ width: '100%' }}
                                                                >
                                                                    <div className="catalog-pdf-preview" style={{ width: '100%' }}>
                                                                        <img
                                                                            src={image.catalogImageUrl}
                                                                            alt={image.catalogImageName || `Catalog Page ${index + 1}`}
                                                                            loading="eager"
                                                                            decoding="async"
                                                                            style={{ width: '100%', height: '580px', objectFit: 'contain', borderRadius: '15px', cursor: 'pointer', boxShadow: '0 12px 35px rgba(0,0,0,0.4)' }}
                                                                        />
                                                                        <div className="catalog-overlay">
                                                                            <div className="catalog-icon">
                                                                                <i className="fas fa-eye"></i>
                                                                            </div>
                                                                            <p>{catalogTranslationsObj.clickToViewFullCatalog}</p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div
                                                            className="swiper-slide"
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                flexShrink: 0
                                                            }}
                                                        >
                                                            <div style={{ width: '100%', height: '750px', backgroundColor: '#f0f0f0', borderRadius: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: '-100%',
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                                                    animation: 'shimmer 1.5s infinite'
                                                                }}></div>
                                                                <p style={{ position: 'relative', zIndex: 1, color: '#666' }}>Loading catalog...</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {/* Fallback for when Swiper is not initialized yet or mobile */}
                                    {!isDesktop && (
                                        <div
                                            className="catalog-item catalog-item-fallback"
                                        onClick={() => (window as any).openCatalogModal?.()}
                                    >
                                        {currentImage && getCatalogText(currentImage, language) && (
                                            <h3 className="catalog-image-text-overlay" style={{
                                                margin: '60px 0 80px 0',
                                                color: '#fff',
                                                fontSize: '26px',
                                                fontWeight: 700,
                                                textAlign: 'center',
                                                lineHeight: '1.2em',
                                                textTransform: 'none',
                                                pointerEvents: 'none'
                                            }}>
                                                {renderCatalogText(getCatalogText(currentImage, language))}
                                            </h3>
                                        )}
                                        <div className="catalog-pdf-preview" style={{ position: 'relative' }}>
                                            {currentImage ? (
                                                <img
                                                    id="catalogImage"
                                                    src={currentImage.catalogImageUrl}
                                                    alt={currentImage.catalogImageName || 'Catalog Preview'}
                                                    loading="eager"
                                                    decoding="async"
                                                    style={{ width: '100%', height: '750px', objectFit: 'cover', borderRadius: '15px', cursor: 'pointer', boxShadow: '0 12px 35px rgba(0,0,0,0.4)' }}
                                                />
                                            ) : (
                                                <div style={{ width: '100%', height: '750px', backgroundColor: '#f0f0f0', borderRadius: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: '-100%',
                                                        width: '100%',
                                                        height: '100%',
                                                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                                        animation: 'shimmer 1.5s infinite'
                                                    }}></div>
                                                    <p style={{ position: 'relative', zIndex: 1, color: '#666' }}>Loading catalog...</p>
                                                </div>
                                            )}
                                            <div className="catalog-overlay">
                                                <div className="catalog-icon">
                                                    <i className="fas fa-eye"></i>
                                                </div>
                                                <p>{catalogTranslationsObj.clickToViewFullCatalog}</p>
                                            </div>
                                        </div>
                                    </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <CatalogPreviewModal
                isOpen={isModalOpen}
                currentPage={modalCurrentPage}
                catalogImages={catalogImages}
                language={language}
                onClose={() => setIsModalOpen(false)}
                onPageChange={handleModalPageChange}
            />
        </>
    );
};

export default CatalogSection;
